import { usePhoto } from "./photos.ts";
import type { CSSProperties, ReactNode } from "react";
import { pageStatus, type Page, type Urgency } from "@pagelink/shared";

/* ---------------------------------------------------------------------------
 * The small pieces every screen is built from: icons, avatar, pills, buttons,
 * section labels, the status stepper. Shapes and sizes follow the product
 * simulation so the live app and the demo read as one system.
 * ------------------------------------------------------------------------ */

type IconPart = [tag: "path" | "rect" | "circle", d: string | null, props?: Record<string, number | string>];
const ICONS: Record<string, IconPart[]> = {
  pager: [["rect", null, { x: 2.5, y: 5, width: 19, height: 14, rx: 2.5 }],
          ["rect", null, { x: 5.5, y: 8.5, width: 9, height: 4.5, rx: 0.7 }],
          ["path", "M17.8 9h1.6M17.8 12h1.6M17.8 15h1.6"]],
  calendar: [["rect", null, { x: 3, y: 4, width: 18, height: 18, rx: 2 }], ["path", "M16 2v4M8 2v4M3 10h18"]],
  users: [["path", "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"], ["circle", null, { cx: 9, cy: 7, r: 4 }], ["path", "M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"]],
  user: [["path", "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"], ["circle", null, { cx: 12, cy: 7, r: 4 }]],
  archive: [["path", "M21 8v13H3V8"], ["rect", null, { x: 1, y: 3, width: 22, height: 5, rx: 1 }], ["path", "M10 12h4"]],
  incoming: [["path", "M17 7 7 17"], ["path", "M17 17H7V7"]],
  outgoing: [["path", "M7 17 17 7"], ["path", "M7 7h10v10"]],
  camera: [["path", "M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"], ["circle", null, { cx: 12, cy: 13, r: 4 }]],
  trash: [["path", "M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"], ["path", "M10 11v6M14 11v6"]],
  restore: [["path", "M3 12a9 9 0 1 0 3-6.7L3 8"], ["path", "M3 3v5h5"]],
  star: [["path", "M12 2.6l2.9 5.88 6.5.95-4.7 4.58 1.11 6.47L12 17.42l-5.81 3.06 1.11-6.47-4.7-4.58 6.5-.95z"]],
  check: [["path", "M20 6 9 17l-5-5"]],
  search: [["circle", null, { cx: 11, cy: 11, r: 7 }], ["path", "m20 20-3.5-3.5"]],
  x: [["path", "M18 6 6 18M6 6l12 12"]],
  alert: [["path", "M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"], ["path", "M12 9v4M12 17h.01"]],
  back: [["path", "M19 12H5M12 19l-7-7 7-7"]],
  info: [["circle", null, { cx: 12, cy: 12, r: 9 }], ["path", "M12 16v-4M12 8h.01"]],
  phone: [["path", "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"]],
  plus: [["path", "M12 5v14M5 12h14"]],
  more: [["circle", null, { cx: 5, cy: 12, r: 1.4 }], ["circle", null, { cx: 12, cy: 12, r: 1.4 }], ["circle", null, { cx: 19, cy: 12, r: 1.4 }]],
  edit: [["path", "M12 20h9"], ["path", "M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"]],
  receipt: [["path", "M5 2h14v20l-3-2-2 2-2-2-2 2-2-2-3 2z"], ["path", "M9 7h6M9 11h6M9 15h4"]],
  chat: [["path", "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"]],
  send: [["path", "M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z"]],
  clock: [["circle", null, { cx: 12, cy: 12, r: 9 }], ["path", "M12 7v5l3 2"]],
  bell: [["path", "M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"], ["path", "M13.73 21a2 2 0 0 1-3.46 0"]],
  exit: [["path", "M16 17l5-5-5-5M21 12H9"], ["path", "M13 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8"]],
  moon: [["path", "M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"]],
  sun: [["circle", null, { cx: 12, cy: 12, r: 4 }], ["path", "M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"]],
};

export function Icon({ name, size = 16, sw = 1.75, fill = "none", style }: {
  name: keyof typeof ICONS | string; size?: number; sw?: number; fill?: string; style?: CSSProperties;
}) {
  const parts = ICONS[name];
  if (!parts) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor"
      strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
      style={{ display: "block", flexShrink: 0, ...style }}>
      {parts.map(([tag, d, props], i) =>
        tag === "path" ? <path key={i} d={d ?? undefined} {...props} />
        : tag === "rect" ? <rect key={i} {...props} />
        : <circle key={i} {...props} />
      )}
    </svg>
  );
}

/* --- division colour: a service never appears without its name beside it --- */
const DIV: Record<string, string> = {
  "Neurosurgery": "#1F5FA8", "Cardiology": "#D9902A", "General Surgery": "#A8342F",
  "Infectious Disease": "#5FA82F", "Plastic Surgery": "#007E9B", "Neurology": "#DE76A6",
  "Intensive Care": "#157C38", "Pediatrics": "#9C79D6", "Obstetrics & Gynaecology": "#6B3FA0",
  "Psychiatry": "#C4703C", "Radiology": "#8A4A12", "Oncology": "#2FA8B5", "Internal Medicine": "#3B6F9C",
  "Emergency": "#B8461E", "Anesthesia": "#4C7A5A", "Orthopedics": "#7A5230",
};
const hash = (s: string) => { let h = 0; for (const c of s) h = (h * 31 + c.charCodeAt(0)) >>> 0; return h; };
// The division hue is mixed with the text colour so that, used as text, it
// stays above 4.5:1 (WCAG 1.4.3) in both themes for every hue: 55% was the
// most colour that held for all of them against card, page and alert fills.
export const divColor = (service: string | null | undefined): string =>
  service ? `color-mix(in srgb, ${DIV[service] ?? `hsl(${hash(service) % 360} 42% 44%)`} 55%, var(--ink))` : "var(--faint)";

/* --- avatar: initials on a stable hue --------------------------------------- */
const initials = (name: string) =>
  name.replace(/^Dr\.?\s*/i, "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");

export function Avatar({ name, size = 38, bg, fontSize, id }: { name: string; size?: number; bg?: string; fontSize?: number; id?: string }) {
  const photo = usePhoto(id);
  return (
    <span className="avatar" aria-hidden="true"
      style={{ width: size, height: size, background: bg ?? `hsl(${hash(name) % 360} 48% 42%)`, fontSize: (fontSize ?? size * 0.42) * 0.78 }}>
      {photo ? <img src={photo} alt="" width={size} height={size} /> : initials(name)}
    </span>
  );
}

/* --- pills and chips --------------------------------------------------------- */
export function Pill({ tone, solid, children }: { tone?: "ok" | "stat" | "urgent" | "blue"; solid?: boolean; children: ReactNode }) {
  return <span className={`pill${tone ? ` ${tone}` : ""}${solid ? " solid" : ""}`}>{children}</span>;
}

export function UrgChip({ u, small, muted }: { u: Urgency; small?: boolean; muted?: boolean }) {
  return <span className={`urg${u === "STAT" ? " stat" : ""}${muted ? " muted" : ""}${small ? " sm" : ""}`}>{u}</span>;
}

export function StatusPill({ page, solid }: { page: Page; solid?: boolean }) {
  const s = pageStatus(page);
  const tone =
    s.state === "accepted" || s.state === "done" ? "ok"
    : s.state === "declined" ? "stat"
    : s.state === "seen" ? "urgent"
    : s.state === "cancelled" || s.state === "redirected" ? undefined
    : "blue";
  return <Pill tone={tone} solid={solid}>{s.label}</Pill>;
}

/* --- buttons: one component, three ranks, two sizes -------------------------- */
export function Btn({ onClick, tone, soft, ghost, full, sm, lg, disabled, children, style, type = "button", ariaLabel }: {
  onClick?: () => void; tone?: "ok" | "stat" | "dim"; soft?: boolean; ghost?: boolean; full?: boolean; sm?: boolean; lg?: boolean;
  disabled?: boolean; children: ReactNode; style?: CSSProperties; type?: "button" | "submit"; ariaLabel?: string;
}) {
  const cls = ["btn", tone, soft && "soft", ghost && "ghost", full && "full", sm && "sm", lg && "lg"].filter(Boolean).join(" ");
  return <button type={type} className={cls} onClick={onClick} disabled={disabled} style={style} aria-label={ariaLabel}>{children}</button>;
}

export const Sec = ({ children }: { children: ReactNode }) => <div className="sec">{children}</div>;
export const Note = ({ children, warn }: { children: ReactNode; warn?: boolean }) => <div className={`note${warn ? " warn" : ""}`}>{children}</div>;

export function Back({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return <button className="back" onClick={onClick}><Icon name="back" size={15} sw={2.2} />{children}</button>;
}

export function Banner({ kind, children }: { kind: "warn" | "err" | "info"; children: ReactNode }) {
  return (
    <div className={`banner ${kind}`} role={kind === "err" ? "alert" : "status"}>
      <Icon name={kind === "info" ? "info" : "alert"} size={14} sw={2} style={{ marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

/* --- the status stepper: a dot on a rail, ringed at the stage reached ------- */
export function StatusLine({ page }: { page: Page }) {
  const has = (t: string) => page.events.some((e) => e.type === t);
  const steps: [string, string][] = [["SENT", "Sent"], ["DELIVERED", "Delivered"], ["SEEN", "Seen"], ["ACK", "Answered"], ["COMPLETED", "Done"]];
  const cancelled = has("CANCEL"), declined = has("DECLINE");
  return (
    <div className="steps" aria-label="Progress">
      {steps.map(([k, l], i) => {
        const reached = has(k);
        const next = steps[i + 1];
        const current = reached && !(next && has(next[0]));
        return (
          <span key={k} style={{ display: "contents" }}>
            {i > 0 && <span className={`rail${reached ? " on" : ""}`} />}
            <span className={`step${reached ? " on" : ""}${current ? " cur" : ""}`}><span className="d" /><span className="l">{l}</span></span>
          </span>
        );
      })}
      {cancelled && <Pill>Cancelled by sender</Pill>}
      {has("REDIRECT") && <Pill tone="blue">Redirected</Pill>}
      {declined && <Pill tone="stat">Declined</Pill>}
      {!has("ACK") && !cancelled && !declined && has("ALERT") && <Pill tone="stat">⚠ No response</Pill>}
      {!has("ACK") && !cancelled && !declined && !has("ALERT") && has("REM2") && <Pill tone="stat">Reminder sent</Pill>}
      {!has("ACK") && !cancelled && !declined && has("REM1") && !has("REM2") && !has("ALERT") && <Pill tone="urgent">Reminder 1 sent</Pill>}
      {!page.toId && !cancelled && <Pill tone="urgent">Locating…</Pill>}
    </div>
  );
}

/* --- formatters ---------------------------------------------------------------- */
export const clock = (iso: string | number) =>
  new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });

export const stamp = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).replace(",", " ·");

/** Today: the time. Otherwise: day and month. */
export const when = (iso: string) => {
  const d = new Date(iso), now = new Date();
  return d.toDateString() === now.toDateString() ? clock(iso) : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};
